import { ActionIconProps, Mark, Text, UnstyledButton } from "@mantine/core";
import { modals } from "@mantine/modals";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { PropsWithChildren, forwardRef } from "react";
import useUserStore from "@/modules/users/store";
import { captureException } from "@/services/log";
import { DESAFIOS_COLLECTION_KEY, deleteDesafio } from "../../firestore";

type RemoverButtonProperties = PropsWithChildren<
  ActionIconProps & { onClick?: (event: React.MouseEvent<HTMLButtonElement>) => void; desafio: TDesafio }
>;
type TDeleteResponse = Awaited<ReturnType<typeof deleteDesafio>>;

const RemoverDesafioButton = forwardRef<HTMLButtonElement, RemoverButtonProperties>(
  ({ desafio, children, ...properties }, reference) => {
    const queryClient = useQueryClient();
    const activeEmpresaId = useUserStore((state) => state.activeEmpresaId);

    const { mutate: deleteMutation, isPending } = useMutation<TDeleteResponse, Error>({
      mutationFn: () => {
        if (!activeEmpresaId) {
          throw new Error("Empresa não selecionada");
        }
        return deleteDesafio(activeEmpresaId, desafio);
      },
      onSuccess() {
        return queryClient.invalidateQueries({ queryKey: [DESAFIOS_COLLECTION_KEY] });
      },
      onError(error) {
        captureException(error, true);
      }
    });

    /**
     * Handles the click event on the button.
     * @param event - The event that triggered the click.
     */
    function handleClick(event: React.MouseEvent<HTMLButtonElement>) {
      event.preventDefault();

      if (properties.onClick) {
        properties.onClick(event);
      }

      modals.openConfirmModal({
        title: "Remover desafio",
        centered: true,
        children: (
          <Text size="sm">
            Tem certeza que deseja remover o desafio <Mark>{desafio.titulo}</Mark>
          </Text>
        ),
        labels: {
          confirm: "Sim",
          cancel: "Não"
        },
        confirmProps: { color: "red" },
        onConfirm: () => {
          void deleteMutation();
        }
      });
    }

    return (
      <UnstyledButton disabled={isPending} ref={reference} {...properties} onClick={handleClick}>
        {children}
      </UnstyledButton>
    );
  }
);

RemoverDesafioButton.displayName = "RemoverDesafioButton";

export default RemoverDesafioButton;
